import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  History, 
  Search, 
  Clock, 
  Users, 
  FileText,
  TrendingDown,
  Plus,
  Minus,
  Settings,
} from 'lucide-react';
import { useAssignmentHistory } from '@/hooks/compensation/useAssignmentHistory';
import { useCompanyEmployees } from '@/hooks/leave/useCompanyEmployees';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import type { AssignmentHistoryEntry } from '@/hooks/compensation/useAssignmentHistory';

interface AssignmentHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId?: string;
  ruleId?: string;
}

// Company-wide assignment history hook
function useCompanyAssignmentHistory(limit = 50) {
  return useQuery({
    queryKey: ['compensation', 'company-assignment-history', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('compensation_assignment_history')
        .select(`
          id,
          profile_id,
          action,
          rule_name,
          rule_type,
          performed_at,
          reason,
          old_values,
          new_values,
          performer:profiles!performed_by(
            first_name,
            last_name,
            avatar_url
          ),
          employee:profiles!profile_id(
            first_name,
            last_name,
            avatar_url
          )
        `)
        .order('performed_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map((entry: any) => ({
        id: entry.id,
        profile_id: entry.profile_id,
        action: entry.action,
        rule_name: entry.rule_name,
        rule_type: entry.rule_type,
        performed_at: entry.performed_at,
        reason: entry.reason,
        old_values: entry.old_values || {},
        new_values: entry.new_values || {},
        performer_name: entry.performer 
          ? `${entry.performer.first_name} ${entry.performer.last_name}` 
          : 'System',
        performer_avatar: entry.performer?.avatar_url,
        employee_name: entry.employee 
          ? `${entry.employee.first_name} ${entry.employee.last_name}` 
          : 'Multiple Employees',
        employee_avatar: entry.employee?.avatar_url
      }));
    },
    staleTime: 2 * 60 * 1000,
  });
}

function ActionIcon({ action }: { action: string }) {
  const iconMap = {
    assigned: Plus,
    unassigned: Minus,
    bulk_assigned: Users,
    created: FileText,
    updated: Settings,
    deactivated: TrendingDown
  };
  
  const Icon = iconMap[action as keyof typeof iconMap] || History;
  return <Icon className="h-4 w-4" />;
}

function ActionBadge({ action }: { action: string }) {
  const variants = {
    assigned: 'default',
    unassigned: 'secondary',
    bulk_assigned: 'default',
    created: 'outline',
    updated: 'outline',
    deactivated: 'destructive'
  } as const;
  
  return (
    <Badge variant={variants[action as keyof typeof variants] || 'outline'} className="text-xs">
      {action.replace('_', ' ').toUpperCase()}
    </Badge>
  );
}

function HistoryEntryCard({ entry }: { entry: AssignmentHistoryEntry }) {
  return (
    <Card className="mb-3">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <Avatar className="h-8 w-8">
            <AvatarImage src={entry.performer_avatar} />
            <AvatarFallback>
              {entry.performer_name?.charAt(0) || 'S'}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ActionIcon action={entry.action} />
                <span className="font-medium">{entry.performer_name}</span>
                <ActionBadge action={entry.action} />
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                {format(new Date(entry.performed_at), 'MMM d, HH:mm')}
              </div>
            </div>
            
            <div className="text-sm">
              <span className="font-medium">{entry.rule_name}</span>
              <Badge variant="outline" className="ml-2 text-xs">
                {entry.rule_type.replace('_', ' ')}
              </Badge>
            </div>
            
            <div className="text-sm text-muted-foreground">
              {entry.action === 'assigned' && `Assigned to ${entry.employee_name}`}
              {entry.action === 'unassigned' && `Unassigned from ${entry.employee_name}`}
              {entry.action === 'bulk_assigned' && `Bulk assigned to ${entry.new_values.affected_employees || 'multiple'} employees`}
              {entry.action === 'created' && 'Rule created'}
              {entry.action === 'updated' && 'Rule updated'}
              {entry.action === 'deactivated' && 'Rule deactivated'}
            </div>
            
            {entry.reason && (
              <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                {entry.reason}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function AssignmentHistoryDialog({
  open,
  onOpenChange,
  employeeId,
  ruleId
}: AssignmentHistoryDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('all');
  
  // Reset filters when dialog opens
  useEffect(() => {
    if (open) {
      setSearchQuery('');
      setSelectedEmployeeId('all');
    }
  }, [open]);

  // Get employee list for filter dropdown
  const { data: employees = [] } = useCompanyEmployees();

  // Get history based on scope
  const { data: employeeHistory } = useAssignmentHistory(employeeId, 50);
  const { data: companyHistory } = useCompanyAssignmentHistory(100);
  
  const historyData = employeeId ? employeeHistory : companyHistory;
  
  // Filter history based on employee selection and search
  const filteredHistory = historyData?.filter((entry: AssignmentHistoryEntry) => {
    // Employee filter
    if (selectedEmployeeId !== 'all' && entry.profile_id !== selectedEmployeeId) {
      return false;
    }

    // Text search filter
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      entry.rule_name.toLowerCase().includes(searchLower) ||
      entry.employee_name?.toLowerCase().includes(searchLower) ||
      entry.performer_name?.toLowerCase().includes(searchLower) ||
      entry.rule_type.toLowerCase().includes(searchLower) ||
      entry.action.toLowerCase().includes(searchLower)
    );
  }) || [];

  // Derive active employees that appear in history for the dropdown
  const activeEmployees = employees.filter((emp: any) => emp.status === 'active');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Assignment History
            {employeeId && ' - Employee'}
          </DialogTitle>
          <DialogDescription>
            {employeeId 
              ? 'View compensation rule assignment history for this employee'
              : 'View all compensation rule assignment activities in your company'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Filters */}
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by rule, employee, or action..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            {!employeeId && (
              <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="All Employees" />
                </SelectTrigger>
                <SelectContent className="z-50 bg-popover">
                  <SelectItem value="all">All Employees</SelectItem>
                  {activeEmployees.map((emp: any) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Statistics - dynamic based on filtered data */}
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-3">
                <div className="text-center">
                  <div className="text-2xl font-bold">
                    {filteredHistory.filter(h => h.action === 'assigned' || h.action === 'bulk_assigned').length}
                  </div>
                  <div className="text-xs text-muted-foreground">Assignments</div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-3">
                <div className="text-center">
                  <div className="text-2xl font-bold">
                    {filteredHistory.filter(h => h.action === 'unassigned').length}
                  </div>
                  <div className="text-xs text-muted-foreground">Unassignments</div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-3">
                <div className="text-center">
                  <div className="text-2xl font-bold">
                    {new Set(filteredHistory.map(h => h.rule_name)).size}
                  </div>
                  <div className="text-xs text-muted-foreground">Unique Rules</div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Separator />

          {/* History Timeline */}
          <ScrollArea className="h-96">
            {filteredHistory.length > 0 ? (
              <div className="space-y-3">
                {filteredHistory.map((entry: AssignmentHistoryEntry) => (
                  <HistoryEntryCard key={entry.id} entry={entry} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <History className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No History Found</h3>
                <p className="text-muted-foreground text-center">
                  {searchQuery || selectedEmployeeId !== 'all'
                    ? "No assignment history matches your filter criteria"
                    : "No assignment history available yet"
                  }
                </p>
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
